import * as RadixDialog from '@radix-ui/react-dialog';
import { cx } from 'class-variance-authority';
import { AnimatePresence, motion } from 'framer-motion';
import { forwardRef } from 'react';
import Overlay from '../overlay';
import DrawerFooter from './components/footer/footer';
import DrawerHeader from './components/header/header';
import type { DrawerProps } from './drawer.types';

const Drawer = forwardRef<HTMLDivElement, DrawerProps>(
  (
    {
      children,
      defaultOpen,
      isNested,
      linkButton,
      onBack,
      onClickOutside,
      onClose,
      onEscapeKeyDown,
      onOpenChange,
      preventEscapeKeyDown,
      open = false,
      primaryButton,
      secondaryButton,
      size = 'default',
      subHeaderComponent,
      hideOverlay = false,
      portalElement,
      title,
      headerTooltip,
    },
    ref,
  ) => {
    const shouldRenderFooter = !!primaryButton || !!secondaryButton || !!linkButton;
    const handleEscapeKeyDown = (event: KeyboardEvent) => {
      if (preventEscapeKeyDown) {
        event.preventDefault();
      } else if (onEscapeKeyDown) {
        event.preventDefault();
        onEscapeKeyDown();
      } else {
        onClose();
      }
    };

    return (
      <RadixDialog.Root open={open} onOpenChange={onOpenChange} defaultOpen={defaultOpen}>
        <AnimatePresence>
          {open && (
            <RadixDialog.Portal forceMount container={portalElement}>
              <RadixDialog.Content
                aria-describedby={undefined}
                asChild
                onEscapeKeyDown={handleEscapeKeyDown}
                onPointerDownOutside={onClickOutside || onClose}
              >
                <motion.div
                  ref={ref}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 100 }}
                  transition={{ duration: 0.1 }}
                  className={cx(
                    `
                  fixed flex flex-col overflow-hidden
                  border border-solid border-tertiary
                  bg-primary shadow-lg
                  z-drawer inset-1  
                  w-auto rounded
                  sm:inset-3 sm:left-auto
                  focus-visible:outline-none
                `,
                    {
                      'sm:w-[500px]': size === 'default',
                      'sm:w-[800px]': size === 'large',
                    },
                  )}
                >
                  <DrawerHeader
                    title={title}
                    isNested={isNested}
                    onBack={onBack}
                    onClose={onClose}
                    headerTooltip={headerTooltip}
                  />
                  {subHeaderComponent}
                  <div className="overflow-auto relative flex-1 max-w-full">
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.div
                        initial={{ opacity: 0, x: isNested ? 100 : -100 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: isNested ? 100 : -100 }}
                        transition={{ duration: 0.1 }}
                        key={isNested ? 'nested' : 'main'}
                        className="absolute inset-0 p-4"
                      >
                        {children}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                  <AnimatePresence initial={false}>
                    {shouldRenderFooter && (
                      <DrawerFooter
                        primaryButton={primaryButton}
                        secondaryButton={secondaryButton}
                        linkButton={linkButton}
                        isNested={isNested}
                      />
                    )}
                  </AnimatePresence>
                </motion.div>
              </RadixDialog.Content>
              <RadixDialog.Overlay asChild>
                {hideOverlay ? null : <Overlay isVisible={open} isConfirmation={false} />}
              </RadixDialog.Overlay>
            </RadixDialog.Portal>
          )}
        </AnimatePresence>
      </RadixDialog.Root>
    );
  },
);

Drawer.displayName = 'Drawer';

export default Drawer;
